import { useCallback } from 'react';

import { DocumentSendError, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { VisitMedicalLeaveCreateDraftCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useDataSyncing } from '@dreryk/command-query-react-common';
import { FormContextValues } from '@dreryk/react-common';
import { ActionBar, ButtonVariant, DataSyncWaitingEventInterceptor, useNavTabsContext } from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import { useVisitDocumentContext } from '../../../../../common/visitDocument/VisitDocumentContext';
import { commonTranslationKeys, medicalLeaveTranslationKeys } from '../../../../../translations';
import { MedicalLeavePanelNavParams } from '../../../MedicalLeavePanel';
import { GuardianMedicalLeaveFormBean } from '../../guardianMedicalLeave.types';

import { useMedicalLeaveGuardianApproveCommand } from './GuardianMedicalLeaveSubmitAction.hooks';

type MedicalLeaveSubmitActionProps = {
   readonly formController: FormContextValues<GuardianMedicalLeaveFormBean>;
   readonly onFault: (fault: PueFault) => void;
   readonly onZlaDocumentSendFault: (zlaDocumentSendErrors: DocumentSendError[]) => void;
};

export const GuardianMedicalLeaveSubmitAction = (props: MedicalLeaveSubmitActionProps): JSX.Element => {
   const { executeVisitDocumentCommand } = useVisitDocumentContext();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const { changeTab } = useNavTabsContext<
      MedicalLeavePanelNavParams.LEAVE_FORM | MedicalLeavePanelNavParams.LEAVES_HISTORY
   >();

   const { executeAsync: submitMedicalLeaveGuardian } = useMedicalLeaveGuardianApproveCommand({
      onSuccess: () => {
         props.formController.markPristine();
         changeTab(MedicalLeavePanelNavParams.LEAVES_HISTORY);
      },
      onPueFault: props.onFault,
      onZlaDocumentSendFault: props.onZlaDocumentSendFault,
      onAuthenticationRequiredFault: openAccountAuthorizationModal,
   });

   const handleSubmitMedicalLeaveGuardian = useCallback(
      async (formData: GuardianMedicalLeaveFormBean): Promise<unknown> => {
         return executeVisitDocumentCommand(VisitMedicalLeaveCreateDraftCommand, async (externalId) =>
            submitMedicalLeaveGuardian({ formData: formData, externalId: externalId })
         );
      },
      [executeVisitDocumentCommand, submitMedicalLeaveGuardian]
   );

   return (
      <>
         <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
            {(waiting) => (
               <ActionBar.PrimaryButton
                  label={commonTranslationKeys.submit}
                  variant={ButtonVariant.PRIMARY}
                  onClick={props.formController.handleSubmit(handleSubmitMedicalLeaveGuardian)}
                  disabled={waiting}
                  testId='submit'
                  blockingOperation={medicalLeaveTranslationKeys.common.submittingMedicalLeave}
               />
            )}
         </DataSyncWaitingEventInterceptor>
         {renderAccountAuthorizationnModal({
            onAuthorization: props.formController.handleSubmit(handleSubmitMedicalLeaveGuardian),
         })}
      </>
   );
};
